import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { EventEmitter, Injectable } from '@angular/core';
import { ProMessagingService } from '../pro-messaging.service';
import { MessageModel } from '../../api/models/message-model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ProMessagesResponse } from '../../api/models/pro-messages-response';
import { debounceTime, map } from 'rxjs/operators';
import { ApiConfiguration } from '../../api/api-configuration';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';

export interface MessagesRequest {
  searchText?: string;
  mainPhoneNumber?: string;
  selectedThreadKey?: string;
}

const TAG = 'ProMessagesDatasource';

@Injectable()
export class ProMessagesDatasource implements DataSource<MessageModel | undefined> {
  private _pageSize = 100;
  private _cachedData = Array.from<MessageModel>({ length: 0 });
  private _fetchedPages = new Set<number>();

  private _subscription = new Subscription();
  private _newMessageSubscription = new Subscription();
  private _deletedMessageSubscription = new Subscription();
  private _searchData: MessagesRequest = {};

  public pendingMessagesRequest: Subscription;

  public dataStream = new BehaviorSubject<(MessageModel | undefined)[]>(this._cachedData);
  public fetchPageEmitter = new EventEmitter<boolean>();
  public lastIncomingMessageEmitter = new EventEmitter<MessageModel>();

  public loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  public length = 0;
  public currentPage = 0;
  public lastPage = 0;

  constructor(
    public messagingService: ProMessagingService,
    private httpClient: HttpClient,
    private apiConfig: ApiConfiguration,
  ) {}

  connect(collectionViewer: CollectionViewer): Observable<(MessageModel | undefined)[]> {
    console.log(`[${TAG}] connect datasource`);
    this._subscription = collectionViewer.viewChange.subscribe(async range => {
      console.log(`[${TAG}] range subscription`, range);
      const startPage = this._getPageForIndex(range.start);
      const endPage = this._getPageForIndex(range.end - 1);
      for (let i = startPage; i <= endPage; i++) {
        this.fetchPage(i);
      }
    });

    this.init();

    return this.dataStream;
  }

  init() {
    console.log(`[${TAG}] init datasource`);
    this.loadingSubject.next(false);

    this._newMessageSubscription = this.messagingService.onNewMessageSubject.subscribe(async message => {
      await this.onNewMessage(message);
    });

    this._deletedMessageSubscription = this.messagingService.onDeletedMessageSubject.subscribe(async message => {
      await this.onDeletedMessage(message);
    });
  }

  onFilterDataChange(searchData: MessagesRequest) {
    this._searchData = searchData;

    console.log(`[${TAG}] searchTextEmitter subscription`);
    this._searchData = searchData;
    this._refreshData(0, true).then();
  }

  disconnect(): void {
    console.log(`[${TAG}] disconnect datasource`);
    this._subscription?.unsubscribe();
    this._newMessageSubscription?.unsubscribe();
    this._deletedMessageSubscription?.unsubscribe();

    this.length = 0;
    this.lastPage = 0;
    this._fetchedPages = new Set<number>();

    this._cachedData = Array.from({ length: this.length });
    this.dataStream.next(this._cachedData);
  }

  private _getPageForIndex(index: number): number {
    this.currentPage = Math.floor(index / this._pageSize);
    return this.currentPage;
  }

  private async _refreshData(page: number, resetPages: boolean): Promise<void> {
    console.log(`[${TAG}] _refreshData`);
    if (resetPages) {
      this.resetList();
    }

    this.fetchPage(page);
  }

  resetList() {
    this.length = 0;
    this.lastPage = 0;
    this._fetchedPages = new Set<number>();

    this._cachedData = Array.from<MessageModel>({ length: this.length });
    this.lastIncomingMessageEmitter.emit(this.getLasIncomingWabaMessage());
    this.dataStream.next(this._cachedData);
  }

  private async onDeletedMessage(data: MessageModel): Promise<void> {
    console.log(`[${TAG}] onDeletedMessage`, data);
    if (!data) {
      return;
    }

    const mainPhoneNumber =
      data.direction === 'out'
        ? PhoneNumbersUtils.phoneNumberToNational(data.tnFrom)
        : PhoneNumbersUtils.phoneNumberToNational(data.tnTo);

    if (mainPhoneNumber === this._searchData.mainPhoneNumber) {
      const selectedThreadKey = this._searchData.selectedThreadKey;

      if (!selectedThreadKey) {
        return;
      }

      if (selectedThreadKey === data.threadKey) {
        const messageIdIndex = this._cachedData.findIndex(x => !!x && x.messageId === data.messageId);
        if (messageIdIndex > -1) {
          this._cachedData[messageIdIndex] = data;
          this.length = this._cachedData.length;
          this.dataStream.next(this._cachedData);
          this.fetchPageEmitter.emit(true);
        }
      }
    }
  }

  private async onNewMessage(data: MessageModel): Promise<void> {
    console.log(`[${TAG}] on new msg`, data);
    if (!data) {
      return;
    }

    const mainPhoneNumber =
      data.direction === 'out'
        ? PhoneNumbersUtils.phoneNumberToNational(data.tnFrom)
        : PhoneNumbersUtils.phoneNumberToNational(data.tnTo);

    if (mainPhoneNumber === this._searchData.mainPhoneNumber) {
      const selectedThreadKey = this._searchData.selectedThreadKey;

      if (!selectedThreadKey) {
        return;
      }

      if (selectedThreadKey === data.threadKey) {
        // message is in active chat
        console.log(`[${TAG}] insert new message to current chat`);
        const messageIdIndex = this._cachedData.findIndex(x => !!x && x.messageId === data.messageId);
        if (messageIdIndex > -1) {
          this._cachedData[messageIdIndex] = data;
        } else {
          // check by queue id
          const messageQueueIdIndex = this._cachedData.findIndex(x => !!x && !!x.queueId && x.queueId === data.queueId);
          if (messageQueueIdIndex > -1) {
            this._cachedData[messageQueueIdIndex] = data;
          } else {
            this._cachedData.push(data);
          }
        }

        this.length = this._cachedData.length;
        this.lastIncomingMessageEmitter.emit(this.getLasIncomingWabaMessage());
        this.dataStream.next(this._cachedData);
        this.fetchPageEmitter.emit(true);
      }
    }
  }

  private fetchPage(page: number) {
    console.log(`[${TAG}] _fetchPage ` + page, this._fetchedPages.values());
    if (this._fetchedPages.has(page)) {
      return;
    }
    this._fetchedPages.add(page);

    if (!this._searchData.selectedThreadKey || !this._searchData.mainPhoneNumber) {
      this.resetList();
      this.loadingSubject.next(false);
      return;
    }

    if (this.pendingMessagesRequest) {
      this.loadingSubject.next(false);
      this.pendingMessagesRequest?.unsubscribe();
    }

    const options: any = {
      method: 'post',
      url: this.apiConfig.rootUrl + `/api/advanced-messaging/get-messages`,
      params: new HttpParams(),
      headers: new HttpHeaders(),
    };

    let skipPages = this.lastPage - page;
    if (skipPages < 0) {
      console.error(`pages lost count!!! lastPage: ${this.lastPage} | currentPage: ${page}`);
      skipPages = 0;
    }

    const request = {
      threadKey: this._searchData.selectedThreadKey,
      //broadcastId: selectedGroup.broadcastId,
      limit: this._pageSize,
      skip: this._pageSize * skipPages,
      sort: [{ direction: 'DESC', field: 'time' }],
      mainPhoneNumber: this._searchData.mainPhoneNumber,
      searchText: this._searchData.searchText,
    };

    this.loadingSubject.next(true);
    this.httpClient
      .post<ProMessagesResponse>(options.url, request, {})
      .pipe(map(data => data))
      .subscribe(
        (value: ProMessagesResponse) => {
          if (this.length === 0) {
            this.length = value.total;
            this._cachedData = Array.from<MessageModel>({ length: this.length });
            this.lastPage = this._getPageForIndex(this.length);
            page = this.lastPage;
            this._fetchedPages.add(page);
            if (this.lastPage !== 0) {
              this._fetchedPages.delete(0);
            }
          }

          let pageStart = this.length - request.skip - value.data.length;
          if (pageStart < 0) {
            pageStart = 0;
          }
          console.log(`[${TAG}] pageStart`, pageStart, this.length, value.data.length, request);
          this._cachedData.splice(pageStart, value.data.length, ...value.data.reverse());
          this.lastIncomingMessageEmitter.emit(this.getLasIncomingWabaMessage());
          this.dataStream.next(this._cachedData);

          this.fetchPageEmitter.emit(false);
          this.loadingSubject.next(false);
        },
        error => {
          console.error(`[${TAG}]`, error);
          this.loadingSubject.next(false);
        },
      );
  }

  getLasIncomingWabaMessage(): MessageModel {
    let incomingMessage: MessageModel;

    if (this._cachedData.length === 0) {
      return incomingMessage;
    }

    for (let i = this._cachedData.length - 1; i >= 0; i--) {
      const msg = this._cachedData[i];
      if (!msg) {
        break;
      }

      if (msg.direction === 'in' && msg.eventType === 'whatsapp') {
        incomingMessage = msg;
        break;
      }
    }

    return incomingMessage;
  }
}
